/**
 * Validation + request building for the "Add Prescription via NewRx" flow (IngestNewRxBatch).
 *
 * Kept separate from the component so the rules can be unit-tested. Mirrors the runtime validation
 * in IngestNewRxBatchActivity so the UI fails fast with the same guarantees:
 *   - exactly one of personId / profileId
 *   - at least one item
 *   - each item: prescriberSpi OR inline prescriber (8 required NewRxPrescriber fields)
 *   - each item medication: quantity, daysSupply, numberOfRefills, sigText; when no drugId also ndc + UoM
 *   - no two items share a drugId (or NDC, when no drugId)
 *
 * Drug/NDC precedence: in NDC mode (no drugId) the caller supplies ndc + quantityUnitOfMeasure. In Drug
 * ID mode the server resolves the drug (and infers the unit of measure), but an optional caller NDC is
 * honored as an override of the drug's active NDC; the caller unit of measure is never sent in Drug ID mode.
 */
import { NewRxItemEntry, NewRxMedicationFormState, NewRxPrescriberFormState, isSupportedQuantityUom } from "./NewRxItemTypes";
import type {
  IngestNewRxBatchRequest,
  NewRxBatchItem,
  NewRxMedication,
  NewRxPrescriber,
} from "@amzn/apaqportaljavalambdatypescriptclient";

type IdentitySource = "personId" | "profileId";

export interface ValidationResult {
  valid: boolean;
  /** Index of the offending tab (so the caller can switch to it), or null for request-level errors. */
  itemIndex: number | null;
  message?: string;
}

/**
 * Largest NewRx batch the backend accepts (IngestNewRxBatchActivity / NewRxBatchItemValidator cap).
 * Shared so every caller (standalone page, CPCP recipe builder) enforces the same limit client-side.
 */
export const MAX_NEWRX_ITEMS = 25;

const REQUIRED_PRESCRIBER_FIELDS: { key: keyof NewRxPrescriberFormState; label: string }[] = [
  { key: "npi", label: "NPI" },
  { key: "deaNumber", label: "DEA Number" },
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "addressLine1", label: "Address Line 1" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "postalCode", label: "Postal Code" },
];

function validatePrescriber(p: NewRxPrescriberFormState, index: number): ValidationResult | null {
  if (p.useSpi) {
    if (!p.prescriberSpi.trim()) {
      return { valid: false, itemIndex: index, message: `Prescription ${index + 1}: Prescriber SPI is required` };
    }
    return null;
  }
  for (const f of REQUIRED_PRESCRIBER_FIELDS) {
    if (!String(p[f.key] || "").trim()) {
      return {
        valid: false,
        itemIndex: index,
        message: `Prescription ${index + 1}: prescriber ${f.label} is required`,
      };
    }
  }
  return null;
}

function validateMedication(m: NewRxMedicationFormState, hasDrugId: boolean, index: number): ValidationResult | null {
  // quantity / daysSupply / numberOfRefills are non-negative integers on the model; validate both
  // presence and parseability so a non-numeric value fails fast client-side instead of at the server.
  const numericFields: { value: string; label: string }[] = [
    { value: m.quantity, label: "Quantity" },
    { value: m.daysSupply, label: "Days Supply" },
    { value: m.numberOfRefills, label: "Number of Refills" },
  ];
  for (const f of numericFields) {
    const trimmed = String(f.value || "").trim();
    if (!trimmed) {
      return { valid: false, itemIndex: index, message: `Prescription ${index + 1}: ${f.label} is required` };
    }
    if (!/^\d+$/.test(trimmed)) {
      return {
        valid: false,
        itemIndex: index,
        message: `Prescription ${index + 1}: ${f.label} must be a non-negative whole number`,
      };
    }
  }
  if (!m.sigText.trim()) {
    return { valid: false, itemIndex: index, message: `Prescription ${index + 1}: Sig Text is required` };
  }
  if (!hasDrugId) {
    if (!m.ndc.trim()) {
      return { valid: false, itemIndex: index, message: `Prescription ${index + 1}: NDC is required when no Drug is selected` };
    }
    if (!m.quantityUnitOfMeasure.trim()) {
      return {
        valid: false,
        itemIndex: index,
        message: `Prescription ${index + 1}: Quantity Unit of Measure is required when no Drug is selected`,
      };
    }
    if (!isSupportedQuantityUom(m.quantityUnitOfMeasure)) {
      return {
        valid: false,
        itemIndex: index,
        message: `Prescription ${index + 1}: Quantity Unit of Measure must be selected from the list`,
      };
    }
  }
  return null;
}

/**
 * Validates the NewRx items themselves — the identity-free rules shared by the standalone page and the
 * CPCP recipe builder. Mirrors IngestNewRxBatchActivity / NewRxBatchItemValidator per-item rules:
 *   - each item: prescriberSpi OR inline prescriber; a drug selected (Drug ID mode) or ndc + UoM (NDC mode)
 *   - medication quantity/daysSupply/numberOfRefills (non-negative integers) + sigText
 *   - no two items share a drugId (or NDC, when no drugId)
 *   - at most MAX_NEWRX_ITEMS items
 *
 * Does NOT check patient identity or require a non-empty list — callers layer those on (the standalone
 * page requires identity + ≥1 item; the recipe builder treats an empty list as "no NewRx").
 */
export function validateNewRxItems(items: NewRxItemEntry[]): ValidationResult {
  if (items.length > MAX_NEWRX_ITEMS) {
    return {
      valid: false,
      itemIndex: null,
      message: `A batch may contain at most ${MAX_NEWRX_ITEMS} prescriptions, but ${items.length} were provided`,
    };
  }

  const seenCorrelationKeys = new Set<string>();
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const useDrugId = item.drugSource === "drugId";

    const prescriberError = validatePrescriber(item.prescriber, i);
    if (prescriberError) return prescriberError;

    // In Drug ID mode a drug must be selected; in NDC mode ndc + UoM are required.
    if (useDrugId && !item.drugId.trim()) {
      return { valid: false, itemIndex: i, message: `Prescription ${i + 1}: a Drug must be selected` };
    }

    const medicationError = validateMedication(item.medication, useDrugId, i);
    if (medicationError) return medicationError;

    // Correlation key: drugId in Drug ID mode, else NDC. Duplicates cannot be attributed on correlation.
    // Note: a cross-mode collision (an item's drugId resolving server-side to another item's raw NDC) can't be
    // detected here without the drugId→NDC lookup, so that residual case is enforced by the runtime instead.
    const key = useDrugId ? `drug:${item.drugId.trim()}` : `ndc:${item.medication.ndc.trim()}`;
    if (seenCorrelationKeys.has(key)) {
      const dupLabel = useDrugId ? "drug" : "NDC";
      return {
        valid: false,
        itemIndex: i,
        message: `Prescription ${i + 1}: duplicate ${dupLabel} in the batch — each prescription must use a distinct drug/NDC`,
      };
    }
    seenCorrelationKeys.add(key);

    // Two Drug ID items with distinct drugIds but the same explicit NDC override would be emitted with
    // identical NDCs — reject that too, so the batch never carries duplicate NDCs.
    if (useDrugId && item.medication.ndc.trim()) {
      const ndcKey = `ndc:${item.medication.ndc.trim()}`;
      if (seenCorrelationKeys.has(ndcKey)) {
        return {
          valid: false,
          itemIndex: i,
          message: `Prescription ${i + 1}: duplicate NDC override in the batch — each prescription must use a distinct NDC`,
        };
      }
      seenCorrelationKeys.add(ndcKey);
    }
  }

  return { valid: true, itemIndex: null };
}

/**
 * Validates the whole standalone-page form. Returns the first failure (with the offending tab index) or a
 * valid result. Identity + non-empty-list checks are specific to the standalone IngestNewRxBatch call; the
 * per-item rules are delegated to the shared {@link validateNewRxItems}.
 */
export function validateNewRxBatch(
  identityValue: string,
  identitySource: IdentitySource,
  items: NewRxItemEntry[]
): ValidationResult {
  const identityLabel = identitySource === "personId" ? "Person ID" : "Profile ID";
  if (!identityValue.trim()) {
    return { valid: false, itemIndex: null, message: `${identityLabel} is required` };
  }
  if (items.length === 0) {
    return { valid: false, itemIndex: null, message: "At least one prescription is required" };
  }
  return validateNewRxItems(items);
}

function toIntOrUndefined(value: string): number | undefined {
  const trimmed = String(value || "").trim();
  if (!trimmed) return undefined;
  const n = parseInt(trimmed, 10);
  return Number.isNaN(n) ? undefined : n;
}

function buildMedication(m: NewRxMedicationFormState, hasDrugId: boolean): NewRxMedication {
  const med: NewRxMedication = {
    quantity: toIntOrUndefined(m.quantity),
    daysSupply: toIntOrUndefined(m.daysSupply),
    numberOfRefills: toIntOrUndefined(m.numberOfRefills),
    sigText: m.sigText.trim(),
  };
  if (!hasDrugId) {
    // NDC mode: no drug lookup, so the caller supplies both the NDC and the unit of measure.
    if (m.ndc.trim()) med.ndc = m.ndc.trim();
    if (m.quantityUnitOfMeasure.trim()) med.quantityUnitOfMeasure = m.quantityUnitOfMeasure.trim();
  } else if (m.ndc.trim()) {
    // Drug ID mode: the server resolves the drug (and infers the unit of measure), but an explicitly
    // entered NDC override is honored — the backend uses the caller NDC over the drug's active NDC. We
    // still never send a caller unit of measure here (it is inferred from the drug), and the override is
    // a deliberate, visible input, so it is not the stale-value hazard the Drug ID mode otherwise avoids.
    med.ndc = m.ndc.trim();
  }
  // Advanced / optional fields (safe to send with or without a drugId).
  if (m.drugDescription.trim()) med.drugDescription = m.drugDescription.trim();
  if (m.drugDbCode.trim()) med.drugDbCode = m.drugDbCode.trim();
  if (m.drugDbQualifier.trim()) med.drugDbQualifier = m.drugDbQualifier.trim();
  if (m.strengthValue.trim()) med.strengthValue = m.strengthValue.trim();
  if (m.quantityCodeListQualifier.trim()) med.quantityCodeListQualifier = m.quantityCodeListQualifier.trim();
  const substitutions = toIntOrUndefined(m.substitutions);
  if (substitutions !== undefined) med.substitutions = substitutions;
  if (m.writtenDate.trim()) med.writtenDate = m.writtenDate.trim();
  return med;
}

function buildPrescriber(p: NewRxPrescriberFormState): NewRxPrescriber {
  const prescriber: NewRxPrescriber = {
    npi: p.npi.trim(),
    deaNumber: p.deaNumber.trim(),
    firstName: p.firstName.trim(),
    lastName: p.lastName.trim(),
    addressLine1: p.addressLine1.trim(),
    city: p.city.trim(),
    state: p.state.trim(),
    postalCode: p.postalCode.trim(),
  };
  if (p.stateLicenseNumber.trim()) prescriber.stateLicenseNumber = p.stateLicenseNumber.trim();
  if (p.suffix.trim()) prescriber.suffix = p.suffix.trim();
  if (p.specialty.trim()) prescriber.specialty = p.specialty.trim();
  if (p.practiceName.trim()) prescriber.practiceName = p.practiceName.trim();
  if (p.countryCode.trim()) prescriber.countryCode = p.countryCode.trim();
  if (p.phone.trim()) prescriber.phone = p.phone.trim();
  if (p.fax.trim()) prescriber.fax = p.fax.trim();
  if (p.email.trim()) prescriber.email = p.email.trim();
  return prescriber;
}

/**
 * Maps NewRx form entries to the wire `NewRxBatchItem[]` — identity-free, so it is shared by the standalone
 * page (which wraps it with personId/profileId) and the CPCP recipe builder (which emits it as a recipe
 * param, letting CPCP mint the customer). Assumes the entries already passed {@link validateNewRxItems}.
 */
export function buildNewRxBatchItems(items: NewRxItemEntry[]): NewRxBatchItem[] {
  return items.map((entry) => {
    const useDrugId = entry.drugSource === "drugId";
    const item: NewRxBatchItem = {
      medication: buildMedication(entry.medication, useDrugId),
      stopAtStep: entry.stopAtStep,
    };
    if (useDrugId) item.drugId = entry.drugId.trim();
    if (entry.prescriber.useSpi) item.prescriberSpi = entry.prescriber.prescriberSpi.trim();
    else item.prescriber = buildPrescriber(entry.prescriber);
    return item;
  });
}

/**
 * Builds the IngestNewRxBatchRequest body for the standalone page. Assumes validateNewRxBatch already passed.
 */
export function buildNewRxBatchRequest(
  identityValue: string,
  identitySource: IdentitySource,
  items: NewRxItemEntry[]
): IngestNewRxBatchRequest {
  const request: IngestNewRxBatchRequest = { items: buildNewRxBatchItems(items) };
  request[identitySource] = identityValue.trim();
  return request;
}
